import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trophy, Medal, Award, Users, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TVDisplay() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [classRanking, setClassRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [countdown, setCountdown] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      const [leaderboardRes, classRes] = await Promise.all([
        axios.get(`${API}/public/leaderboard`),
        axios.get(`${API}/public/class-ranking`),
      ]);
      setLeaderboard(leaderboardRes.data);
      setClassRanking(classRes.data);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setCountdown(30);
  }, []);

  useEffect(() => {
    fetchData();
    const dataInterval = setInterval(fetchData, 30000);
    const countdownInterval = setInterval(() => {
      setCountdown(prev => prev > 0 ? prev - 1 : 30);
    }, 1000);
    
    return () => {
      clearInterval(dataInterval);
      clearInterval(countdownInterval);
    };
  }, [fetchData]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#121233]">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#6490E8] mx-auto mb-4 animate-spin" />
          <p className="text-2xl text-white">Жүктелуде...</p>
        </div>
      </div>
    );
  }

  const getRankBadge = (rank) => {
    if (rank === 1) return (
      <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
        <Trophy className="w-8 h-8 text-yellow-900" />
      </div>
    );
    if (rank === 2) return (
      <div className="w-12 h-12 bg-gradient-to-br from-gray-300 to-gray-500 rounded-full flex items-center justify-center shadow-md">
        <Medal className="w-7 h-7 text-gray-700" />
      </div>
    );
    if (rank === 3) return (
      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-md">
        <Award className="w-7 h-7 text-amber-900" />
      </div>
    );
    return (
      <div className="w-10 h-10 bg-[#2a2a5a] rounded-full flex items-center justify-center border border-[#6490E8]/30">
        <span className="text-lg font-bold text-white/70">{rank}</span>
      </div>
    );
  };

  return (
    <div 
      className="min-h-screen bg-[#121233] p-6 cursor-pointer"
      onClick={toggleFullscreen}
      data-testid="tv-display"
    >
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a1f] via-[#121233] to-[#1a1a3e] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <header className="text-center mb-8">
          <div className="flex items-center justify-center gap-6 mb-4">
            <img 
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_a05533bd-d761-4a3f-beb7-e7e62bdd4f88/artifacts/glvqb9sk_ChatGPT%20Image%20Aug%2013%2C%202026%20at%2002_26_31%20PM.png"
              alt="Logo"
              className="w-20 h-20 object-contain"
            />
            <div>
              <h1 className="text-5xl font-bold text-white heading-font tracking-tight">
                Merit <span className="text-[#6490E8]">Plus</span>
              </h1>
              <p className="text-xl text-[#C9B291] font-medium">Qzylorda Er BIL</p>
            </div>
          </div>
          
          {/* Status bar */}
          <div className="flex items-center justify-center gap-4 text-white/60 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span>LIVE</span>
            </div>
            <span>•</span>
            <span>Жаңарту: {countdown}с</span>
            <span>•</span>
            <span>{lastUpdate.toLocaleTimeString('kk-KZ')}</span>
          </div>
        </header>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Students Leaderboard */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <Trophy className="w-7 h-7 text-yellow-400" />
              <h2 className="text-2xl font-bold text-white heading-font">
                Үздік 10 оқушы
              </h2>
            </div>
            
            <div className="space-y-3">
              {leaderboard.slice(0, 10).map((student, index) => (
                <div 
                  key={student.student_id}
                  className={`
                    rounded-xl p-4 
                    bg-white/5 border border-white/10
                    ${index === 0 ? 'bg-gradient-to-r from-yellow-500/10 to-transparent' : ''}
                    ${index === 1 ? 'bg-gradient-to-r from-gray-400/10 to-transparent' : ''}
                    ${index === 2 ? 'bg-gradient-to-r from-amber-500/10 to-transparent' : ''}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getRankBadge(index + 1)}
                      <div>
                        <h3 className={`font-bold text-white ${index < 3 ? 'text-xl' : 'text-lg'}`}>
                          {student.student_name}
                        </h3>
                        <p className="text-sm text-[#C9B291]">{student.class_name}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`
                        font-bold heading-font
                        ${index === 0 ? 'text-4xl text-yellow-400' : ''}
                        ${index === 1 ? 'text-3xl text-gray-300' : ''}
                        ${index === 2 ? 'text-3xl text-amber-400' : ''}
                        ${index > 2 ? 'text-2xl text-[#6490E8]' : ''}
                      `}>
                        {student.total_score > 0 ? '+' : ''}{student.total_score}
                      </div>
                      <p className="text-xs text-white/50">ұпай</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Class Rankings */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <Users className="w-7 h-7 text-[#6490E8]" />
              <h2 className="text-2xl font-bold text-white heading-font">
                Сыныптар рейтингі
              </h2>
            </div>
            
            <div className="space-y-3">
              {classRanking.slice(0, 10).map((classItem, index) => (
                <div 
                  key={classItem.class_id}
                  className={`
                    rounded-xl p-4
                    bg-white/5 border border-white/10
                    ${index === 0 ? 'bg-gradient-to-l from-yellow-500/10 to-transparent' : ''}
                    ${index === 1 ? 'bg-gradient-to-l from-gray-400/10 to-transparent' : ''}
                    ${index === 2 ? 'bg-gradient-to-l from-amber-500/10 to-transparent' : ''}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getRankBadge(index + 1)}
                      <div>
                        <h3 className={`font-bold text-white ${index < 3 ? 'text-xl' : 'text-lg'}`}>
                          {classItem.class_name}
                        </h3>
                        <p className="text-sm text-white/50">{classItem.student_count} оқушы</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`
                        font-bold heading-font
                        ${index === 0 ? 'text-4xl text-yellow-400' : ''}
                        ${index === 1 ? 'text-3xl text-gray-300' : ''}
                        ${index === 2 ? 'text-3xl text-amber-400' : ''}
                        ${index > 2 ? 'text-2xl text-[#6490E8]' : ''}
                      `}>
                        {classItem.average_score > 0 ? '+' : ''}{classItem.average_score.toFixed(1)}
                      </div>
                      <p className="text-xs text-white/50">орташа</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-8 text-center">
          <p className="text-white/40 text-sm">Там экранға ауысу үшін басыңыз</p>
        </footer>
      </div>
    </div>
  );
}
